import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

function Intelligence() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(searchParams.get('case_id') || '');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/hdp/cases').then((res) => {
      setCases(res.data.cases || []);
    });
  }, []);

  useEffect(() => {
    if (!selectedCaseId) {
      setReport(null);
      return;
    }

    setLoading(true);
    api.get('/hdp/reports/intelligence', { params: { case_id: selectedCaseId } })
      .then((res) => setReport(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedCaseId]);

  const handleCaseChange = (caseId) => {
    setSelectedCaseId(caseId);
    if (caseId) {
      setSearchParams({ case_id: caseId });
    } else {
      setSearchParams({});
    }
  };

  const downloadReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hdp-intelligence-${selectedCaseId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Intelligence Reports</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={downloadReport} disabled={!report}>
            Export JSON
          </button>
          <a className="btn btn-secondary" href="/api/reports/agents" target="_blank" rel="noreferrer">
            Agent Export
          </a>
        </div>
      </header>

      <div className="main-body">
        <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="card-body">
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Generate client-facing investigation summaries from case files, personas, evidence,
              and enriched indicators. Reports are generated on demand — archive the underlying case
              or delete its contents to change what appears in future exports.
            </p>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="card-header">
            <h3 className="card-title">Select Case</h3>
          </div>
          <div className="card-body">
            <select
              className="form-input"
              value={selectedCaseId}
              onChange={(e) => handleCaseChange(e.target.value)}
              style={{ maxWidth: '480px' }}
            >
              <option value="">Choose a case...</option>
              {cases.map((item) => (
                <option key={item.case_id} value={item.case_id}>{item.title}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <p style={{ color: 'var(--text-muted)' }}>Generating report...</p>
        )}

        {report && !loading && (
          <>
            <div className="stats-grid" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div className="stat-card">
                <div className="stat-label">Personas</div>
                <div className="stat-value">{report.personas.length}</div>
              </div>
              <div className="stat-card cyan">
                <div className="stat-label">Evidence Items</div>
                <div className="stat-value">{report.evidence.length}</div>
              </div>
              <div className="stat-card purple">
                <div className="stat-label">Indicators</div>
                <div className="stat-value">{report.indicators.length}</div>
              </div>
              <div className="stat-card orange">
                <div className="stat-label">Case Status</div>
                <div className="stat-value" style={{ fontSize: '24px' }}>{report.case.status}</div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">{report.case.title}</h3>
              </div>
              <div className="card-body">
                <p style={{ color: 'var(--text-secondary)' }}>
                  {report.case.description || 'No case description provided.'}
                </p>
                <div style={{ marginTop: 'var(--spacing-lg)' }}>
                  <h4 style={{ color: 'var(--neon-cyan)' }}>Report Preview</h4>
                  <pre style={{
                    background: 'var(--bg-secondary)',
                    padding: 'var(--spacing-md)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'auto',
                    maxHeight: '480px',
                    fontSize: '12px',
                  }}>
                    {JSON.stringify(report, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Intelligence;
